import { useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Modal } from '@/shared/ui/Modal/Modal'
import { fecha as fechaCorta, pesos } from '@/shared/utilidades/formato'

import { useEnlazarFactura, useFacturasParaEnlazar } from './useDocumentosDeLaObra'
import estilos from './EnlazarFactura.module.css'

/**
 * COLGARLE A LA OBRA LA FACTURA CON QUE SE COBRÓ.
 *
 * Una obra se cotiza, se ejecuta y se factura —a veces en varias facturas—, y
 * hasta que no se ve junto nadie sabe si lo que se cobró corresponde con lo
 * que se aprobó. Esa pregunta se contestaba abriendo World Office al lado.
 *
 * Se ofrecen primero las del cliente de la orden y posteriores a la visita,
 * que es lo que una factura de esta obra tiene que ser. Lo demás se busca a
 * mano: por nombre, por número o por CUFE, que es como llega una consulta de
 * la DIAN.
 */
export function EnlazarFactura({
  ordenServicioId,
  clienteNombre,
  abierto,
  onCerrar,
}: {
  ordenServicioId: string
  clienteNombre: string
  abierto: boolean
  onCerrar: () => void
}) {
  const [soloDelCliente, setSoloDelCliente] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [elegida, setElegida] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { candidatas, cargando } = useFacturasParaEnlazar(abierto ? ordenServicioId : null, {
    soloDelCliente,
    busqueda,
  })
  const enlazar = useEnlazarFactura(ordenServicioId)

  async function confirmar() {
    if (elegida === null) return
    setError(null)

    try {
      await enlazar.mutateAsync({ idAsiento: elegida })
      setElegida(null)
      onCerrar()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo enlazar la factura.')
    }
  }

  return (
    <Modal
      abierto={abierto}
      titulo="Enlazar una factura de venta"
      subtitulo={`Las de World Office. Se ofrecen primero las de ${clienteNombre}.`}
      ancho
      onCerrar={onCerrar}
      pie={
        <>
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante="principal"
            onClick={() => void confirmar()}
            trabajando={enlazar.isPending}
            disabled={elegida === null}
          >
            Enlazar
          </Boton>
        </>
      }
    >
      {error && <Aviso tipo="error">{error}</Aviso>}

      <div className={estilos.filtros}>
        <input
          className={estilos.buscador}
          type="search"
          placeholder="Número, cliente o CUFE…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          aria-label="Buscar una factura"
        />
        <label className={estilos.alternar}>
          <input
            type="checkbox"
            checked={soloDelCliente}
            onChange={(e) => setSoloDelCliente(e.target.checked)}
          />
          Solo las de este cliente
        </label>
      </div>

      {cargando ? (
        <p className={estilos.vacio}>Buscando…</p>
      ) : candidatas.length === 0 ? (
        <Aviso tipo="info">
          {soloDelCliente
            ? 'Ninguna factura de este cliente está libre. Quite el filtro para ver las demás.'
            : 'No hay facturas que coincidan. Puede que la sincronización todavía no las haya traído.'}
        </Aviso>
      ) : (
        <ul className={estilos.lista}>
          {candidatas.map((f) => (
            <li key={f.id_asiento}>
              <button
                type="button"
                className={`${estilos.opcion} ${elegida === f.id_asiento ? estilos.elegida : ''}`}
                onClick={() => setElegida(f.id_asiento)}
              >
                <span className={estilos.izquierda}>
                  <span className={estilos.numero}>{f.numero_visible}</span>
                  <span className={estilos.cliente}>{f.cliente_nombre ?? 'Sin cliente'}</span>
                  {/* El porqué de la sugerencia, dicho: una lista que
                      recomienda sin explicar enseña a desconfiar de ella. */}
                  {f.motivo && <span className={estilos.motivo}>{f.motivo}</span>}
                </span>

                <span className={estilos.derecha}>
                  <span className={estilos.plata}>{pesos(f.total)}</span>
                  <span className={estilos.fecha}>{f.fecha ? fechaCorta(f.fecha) : '—'}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
